import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { LogBody, LogToolbar, LogLine } from '@saucelabs/sl-component-log';

import actions from '../actions';
import operations from '../operations';
import { selectors as testConfigurationSelectors } from '../../testConfiguration';
import { MOBILE_LOG_DISPLAYED_LINES_UPDATE_INTERVAL_MS } from '../constants';
import { TOOLBAR_TRACKING_NAMES, trackEvent } from './../../../tracking';

const LogsContainer = ({
  downloadLog,
  handleChangeMobileAppLog,
  handleClearLog,
  handleExpandMobileLogSearch,
  handleLoadMoreLogLines,
  handlePauseLogLines,
  handleResumeLogLines,
  handleSetLogLevel,
  handleTextWrapMobileLog,
  handleUpdateFilters,
  log,
  session,
  title,
  updateDisplayedMobileLogLines,
  updateSessionApp,
}) => {
  useEffect(() => {
    updateDisplayedMobileLogLines();
    const updateInterval = setInterval(() => {
      updateDisplayedMobileLogLines();
    }, MOBILE_LOG_DISPLAYED_LINES_UPDATE_INTERVAL_MS);
    return () => {
      clearInterval(updateInterval);
    };
  }, [updateDisplayedMobileLogLines]);

  useEffect(() => {
    updateSessionApp(session);
  }, [updateSessionApp, session]);

  const deviceType = useSelector(testConfigurationSelectors.deviceTypeSelector);
  const testType = useSelector(testConfigurationSelectors.testTypeSelector);

  const { params: { deviceId } } = useRouteMatch();

  const app = log.get('app');
  const apps = log.get('apps');
  const bottomHasMore = log.get('bottomHasMore');
  const displayedLogLines = log.get('displayedLines').reduce((acc, line) => [...acc, line.toObject()], []);
  const filter = log.get('filter').toObject();
  const logLevel = log.get('logLevel');
  const paused = log.get('paused');
  const searchExpanded = log.get('searchExpanded');
  const textWrapped = log.get('textWrapped');
  const topHasMore = log.get('topHasMore');

  return (
    <LogToolbar
      app={app}
      apps={apps}
      filter={filter}
      logLevel={logLevel}
      onAppChange={handleChangeMobileAppLog}
      onClearLog={() => {
        handleClearLog();
        trackEvent(TOOLBAR_TRACKING_NAMES.DEVELOPERS_OPTIONS_CLEAR_LOG, {
          deviceType, testType,
        });
      }}
      onClickResume={handleResumeLogLines}
      onDownloadLog={() => {
        downloadLog({ deviceId });
        trackEvent(TOOLBAR_TRACKING_NAMES.DEVELOPERS_OPTIONS_DOWNLOAD_LOG, {
          deviceType, testType,
        });
      }}
      onLogLevelChange={handleSetLogLevel}
      onPause={() => {
        handlePauseLogLines();
        trackEvent(TOOLBAR_TRACKING_NAMES.DEVELOPERS_OPTIONS_PAUSE_LOG, {
          deviceType, testType,
        });
      }}
      onSearchExpanded={() => {
        handleExpandMobileLogSearch();
        trackEvent(TOOLBAR_TRACKING_NAMES.DEVELOPERS_OPTIONS_SEARCH_LOGS, {
          deviceType, testType,
        });
      }}
      onSearchFilterChange={handleUpdateFilters}
      onTextWrapChange={() => {
        handleTextWrapMobileLog();
        trackEvent(TOOLBAR_TRACKING_NAMES.DEVELOPERS_OPTIONS_WRAP_TEXT, {
          deviceType, testType,
        });
      }}
      paused={paused}
      searchExpanded={searchExpanded}
      showClearLogs
      showDownloadLog
      showLogLevels
      showPause
      showSearch
      showTextWrap
      textWrapped={textWrapped}
      title={title}
    >
      <LogBody
        bottomHasMore={bottomHasMore}
        noScrollX={textWrapped}
        onClickLoadMore={handleLoadMoreLogLines}
        onClickResume={handleResumeLogLines}
        onPause={handlePauseLogLines}
        paused={paused}
        searchExpanded={searchExpanded}
        topHasMore={topHasMore}
      >
        {displayedLogLines.map((line) => (
          <LogLine
            {...line}
            textWrapped={textWrapped}
          />
        ))}
      </LogBody>
    </LogToolbar>
  );
};

LogsContainer.propTypes = {
  downloadLog: PropTypes.func.isRequired,
  handleChangeMobileAppLog: PropTypes.func.isRequired,
  handleClearLog: PropTypes.func.isRequired,
  handleExpandMobileLogSearch: PropTypes.func.isRequired,
  handleLoadMoreLogLines: PropTypes.func.isRequired,
  handlePauseLogLines: PropTypes.func.isRequired,
  handleResumeLogLines: PropTypes.func.isRequired,
  handleSetLogLevel: PropTypes.func.isRequired,
  handleTextWrapMobileLog: PropTypes.func.isRequired,
  handleUpdateFilters: PropTypes.func.isRequired,
  log: ImmutablePropTypes.map.isRequired,
  session: ImmutablePropTypes.map.isRequired,
  title: PropTypes.string,
  updateDisplayedMobileLogLines: PropTypes.func.isRequired,
  updateSessionApp: PropTypes.func.isRequired,
};

const mapStateToProps = ({ apps: { manual: { log, session } } }) => ({ log, session });

const mapDispatchToProps = {
  downloadLog: operations.downloadMobileLog,
  handleChangeMobileAppLog: actions.changeMobileAppLog,
  handleClearLog: actions.clearMobileLog,
  handleExpandMobileLogSearch: actions.expandMobileLogSearch,
  handleLoadMoreLogLines: actions.loadMoreMobileLogLines,
  handlePauseLogLines: actions.pauseMobileLogLines,
  handleResumeLogLines: actions.resumeMobileLogLines,
  handleSetLogLevel: actions.setMobileLogLevel,
  handleTextWrapMobileLog: actions.textWrapMobileLog,
  handleUpdateFilters: actions.updateMobileLogFilters,
  updateDisplayedMobileLogLines: actions.updateDisplayedMobileLogLines,
  updateSessionApp: actions.updateSessionApp,
};

LogsContainer.defaultProps = {
  title: '',
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(LogsContainer);
export { LogsContainer, mapStateToProps, mapDispatchToProps };
